/**
 * rs-cart-stale-check.js
 *
 * Keeps cart line item properties in step with the nightly NetSuite sync.
 * Liquid computes every managed value; this element only applies the diff.
 *
 * Two passes:
 *  - Page load: reads the per-line JSON rendered inside each cart line. No fetch.
 *  - Checkout click: re-fetches api-cart-stale-props, so a tab left open past
 *    the batch still gets live values. Shows a dialog when a ship date moved.
 */
if (!customElements.get('rs-cart-stale-check')) {
  const MIGRATION_TARGET = /^_Customization_(\d+)$/;
  const STALE_SECTION = 'api-cart-stale-props';
  const COLLECT_TIMEOUT_MS = 800;
  const SILENT_PASS_TIMEOUT_MS = 8000;

  customElements.define('rs-cart-stale-check', class extends HTMLElement {
    connectedCallback() {
      this._dialog = this.querySelector('[data-stale-dialog]');
      if (!this._dialog) return;

      this._proceedBtn = this._dialog.querySelector('[data-stale-proceed]');
      this._cancelBtn  = this._dialog.querySelector('[data-stale-cancel]');

      this._onSubmit = (e) => {
        if (e.target?.id !== 'cartForm') return;
        if (e.submitter?.name !== 'checkout') return;
        e.preventDefault();
        this._onCheckout(e.submitter);
      };
      document.addEventListener('submit', this._onSubmit, true);

      this._cancelBtn?.addEventListener('click', () => {
        this._dialog.close();
        this._checkoutBtn.disabled = false;
      });

      this._proceedBtn?.addEventListener('click', () => this._applyAndProceed());

      /* Liquid emits a payload only for lines that differ, so one existing is the stale signal */
      const domUpdates = this._readDomUpdates();
      if (domUpdates.length) this._runSilentPass(domUpdates);
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this._onSubmit, true);
    }

    /* ── Passes ───────────────────────────────── */

    /**
     * Shared across instances and held as a promise,
     * so the checkout click can await the pass instead of starting a second one over the same lines.
     */
    _runSilentPass(updates) {
      if (!window.__rsStaleSilentPass) {
        window.__rsStaleSilentPass = this._applyUpdates(updates).catch(() => {});
      }
      return window.__rsStaleSilentPass;
    }

    async _onCheckout(btn) {
      this._checkoutBtn = btn;
      btn.disabled = true;

      try {
        /**
         * The load pass gets its own allowance — those writes are the repair,
         * and abandoning them mid-loop is what puts stale dates on the order.
         */
        await this._withTimeout(window.__rsStaleSilentPass, SILENT_PASS_TIMEOUT_MS);

        const updates = await this._withTimeout(this._fetchLiveUpdates(), COLLECT_TIMEOUT_MS);
        if (!updates?.length) return this._proceed();

        this._pendingUpdates = updates;

        /* Dialog only for a changed ship date; silent metadata fixes just apply */
        if (updates.some(update => update.userFacing)) {
          btn.disabled = false;
          this._dialog.showModal();
        } else {
          await this._applyUpdates(updates);
          this._proceed();
        }
      } catch (_e) {
        this._proceed(btn); // never block checkout on error
      }
    }

    async _applyAndProceed() {
      this._proceedBtn.disabled = true;
      this._proceedBtn.textContent = 'Updating…';
      await this._applyUpdates(this._pendingUpdates);
      this._proceed();
    }

    _proceed(btn) {
      if (btn) btn.disabled = false;
      window.location.href = '/checkout';
    }

    /* ── Reading the diff ─────────────────────── */

    /* Page load — the values are already in the markup, so this costs no request */
    _readDomUpdates() {
      return [...document.querySelectorAll('[data-rs-stale-props]')]
        .map((el) => this._normalizeUpdate(this._safeParse(el.textContent)))
        .filter(Boolean);
    }

    /**
     * Checkout click — one JSON-only section for the whole cart.
     * The DOM payload is as old as the page, so a tab open past the nightly batch needs this.
     */
    async _fetchLiveUpdates() {
      const response = await fetch(`${theme.routes.cart_url}?section_id=${STALE_SECTION}`);
      if (!response.ok) return null;

      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const payload = markup.querySelector('[data-rs-stale-props-all]');
      if (!payload) return null;

      const lines = this._safeParse(payload.textContent)?.lines;
      if (!Array.isArray(lines)) return null;

      return lines.map((line) => this._normalizeUpdate(line)).filter(Boolean);
    }

    _safeParse(text) {
      try {
        return JSON.parse(text);
      } catch (_e) {
        return null;
      }
    }

    _normalizeUpdate(update) {
      if (!update?.key) return null;

      const set = update.set && typeof update.set === 'object' ? update.set : {};
      const remove = Array.isArray(update.remove) ? update.remove : [];

      update.set = Object.fromEntries(
        Object.entries(set).filter(([key]) => this._isWritable(key, remove))
      );
      update.remove = remove.filter((key) => this._isWritable(key, remove));
      update.stored = update.stored && typeof update.stored === 'object' ? update.stored : {};

      if (!Object.keys(update.set).length && !update.remove.length) return null;
      return update;
    }

    /**
     * Liquid decides what changes; this blocks the one thing it must never touch.
     * A customization key passes only as the target of its own lip_{n} migration,
     * which copies a value rather than deriving one.
     */
    _isWritable(key, remove) {
      const migration = key.match(MIGRATION_TARGET);
      if (migration) return remove.includes(`lip_${migration[1]}_customization`);
      return !key.startsWith('_Customization');
    }

    /* ── Writing ──────────────────────────────── */

    /* Resolves rather than rejects — a timed-out check must never block checkout */
    _withTimeout(promise, ms) {
      return Promise.race([
        Promise.resolve(promise).catch(() => null),
        new Promise((resolve) => setTimeout(() => resolve(null), ms))
      ]);
    }

    async _applyUpdates(updates) {
      if (!updates?.length) return;

      /* Sequential by design — concurrent cart writes are not isolated */
      for (const update of updates) {
        const properties = { ...update.stored, ...update.set };
        update.remove.forEach((key) => delete properties[key]);

        try {
          await fetch(`${theme.routes.cart_change_url}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: update.key, quantity: update.quantity, properties })
          });
        } catch (_e) {
          /* One failed line must not strand the rest */
        }
      }
    }
  });
}
